import { apiRequest, apiFetchObjectUrl } from "../../lib/api-client";
import type {
  AdminHost,
  AdminUser,
  AuditLogEntry,
  BillingCycle,
  BillingPlanKey,
  CheckoutSession,
  CompanySettings,
  EmailConfig,
  EmailConfigUpdatePayload,
  EmailTemplate,
  GatePassTemplate,
  PlanInfo,
  Site,
  SiteCreatePayload,
  SiteUpdatePayload,
  Subscription,
  VisitorDetail,
  VisitorFieldConfig,
  VisitorRecord,
  VisitorVisit,
} from "./types";

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : "";
}

export type AdminVisitorFilters = {
  search?: string;
  is_blacklisted?: boolean;
  sort_by?: string;
  sort_dir?: "asc" | "desc";
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
};

export function listAdminVisitors(filters: AdminVisitorFilters = {}) {
  return apiRequest<VisitorRecord[]>(`/admin/visitors${buildQuery(filters)}`);
}

export function updateVisitorBlacklist(visitorId: number, isBlacklisted: boolean) {
  return apiRequest<VisitorRecord>(`/admin/visitors/${visitorId}`, {
    method: "PATCH",
    body: { is_blacklisted: isBlacklisted },
  });
}

// ---- Visitor detail (photo / ID / laptop / visit history) ----

export function getVisitorDetail(visitorId: number) {
  return apiRequest<VisitorDetail>(`/visitors/${visitorId}`);
}

export function getVisitorVisits(visitorId: number) {
  return apiRequest<VisitorVisit[]>(`/visitors/${visitorId}/visits`);
}

export function getVisitorPhotoObjectUrl(visitorId: number) {
  return apiFetchObjectUrl(`/visitors/${visitorId}/photo`);
}

export function getVisitorIdDocumentObjectUrl(visitorId: number) {
  return apiFetchObjectUrl(`/visitors/${visitorId}/id-document`);
}

// ---- Visitor field config (which fields are mandatory) ----

export function getVisitorFieldConfig() {
  return apiRequest<VisitorFieldConfig>("/admin/visitor-fields");
}

export function updateVisitorFieldConfig(requiredFields: string[]) {
  return apiRequest<VisitorFieldConfig>("/admin/visitor-fields", {
    method: "PUT",
    body: { required_fields: requiredFields },
  });
}

export function getRequiredVisitorFields() {
  return apiRequest<{ required_fields: string[] }>("/dashboard/visitor-field-config");
}

// ---- Backup & restore ----

export type CompanyBackup = {
  vms_backup_version: number;
  company_code: string;
  created_at: string;
  tables: Record<string, Record<string, unknown>[]>;
};

export function downloadCompanyBackup() {
  return apiRequest<CompanyBackup>("/admin/backup");
}

export function restoreCompanyBackup(backup: CompanyBackup) {
  return apiRequest<{ restored: Record<string, number>; pre_restore_backup_path: string }>(
    "/admin/restore",
    { method: "POST", body: { ...backup, confirm: "RESTORE" } },
  );
}

// ---- Gate self-registration QR ----

export type GateQr = { token: string | null; register_path: string | null };

export function getGateQr() {
  return apiRequest<GateQr>("/admin/gate-qr");
}

export function regenerateGateQr() {
  return apiRequest<GateQr>("/admin/gate-qr/regenerate", { method: "POST" });
}

// DEMO-only: wipe visitor activity (visitors/visits/logs), keep hosts/sites/config.
export function clearCompanyData() {
  return apiRequest<{ cleared: Record<string, number>; pre_clear_backup_path: string }>(
    "/admin/clear-data",
    { method: "POST", body: { confirm: "CLEAR" } },
  );
}

export type AuditLogFilters = {
  visit_id?: number;
  event_type?: string;
  source?: string;
  date_from?: string;
  date_to?: string;
  sort_dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
};

export function listAuditLogs(filters: AuditLogFilters = {}) {
  return apiRequest<AuditLogEntry[]>(`/audit/logs${buildQuery(filters)}`);
}

export type AdminUserFilters = {
  search?: string;
  role?: string;
  is_active?: boolean;
  sort_by?: string;
  sort_dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
};

export function listAdminUsers(filters: AdminUserFilters = {}) {
  return apiRequest<AdminUser[]>(`/admin/users${buildQuery(filters)}`);
}

export type AdminUserCreatePayload = {
  username: string;
  email?: string;
  password: string;
  role: string;
  is_active: boolean;
  site_id?: number | null;
};

export function createAdminUser(payload: AdminUserCreatePayload) {
  return apiRequest<AdminUser>("/admin/users", {
    method: "POST",
    body: payload,
  });
}

export type AdminUserUpdatePayload = {
  email?: string;
  role?: string;
  is_active?: boolean;
  password?: string;
  site_id?: number | null;
  clear_site?: boolean;
};

export function updateAdminUser(userId: number, payload: AdminUserUpdatePayload) {
  return apiRequest<AdminUser>(`/admin/users/${userId}`, {
    method: "PATCH",
    body: payload,
  });
}

export type AdminHostFilters = {
  search?: string;
  is_active?: boolean;
  sort_by?: string;
  sort_dir?: "asc" | "desc";
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
};

export function listAdminHosts(filters: AdminHostFilters = {}) {
  return apiRequest<AdminHost[]>(`/admin/hosts${buildQuery(filters)}`);
}

export type AdminHostCreatePayload = {
  name: string;
  email?: string;
  phone?: string;
  department?: string;
  is_active: boolean;
  user_id?: number;
  site_id?: number | null;
};

export type HostImportSkippedRow = {
  row: number;
  name?: string | null;
  reason: string;
};

export type HostImportResult = {
  created: number;
  total_rows: number;
  skipped: HostImportSkippedRow[];
};

export function importHosts(file: File) {
  const formData = new FormData();
  formData.append("file", file);
  return apiRequest<HostImportResult>("/admin/hosts/import", {
    method: "POST",
    body: formData,
  });
}

export function createAdminHost(payload: AdminHostCreatePayload) {
  return apiRequest<AdminHost>("/admin/hosts", {
    method: "POST",
    body: payload,
  });
}

export type AdminHostUpdatePayload = {
  name?: string;
  email?: string;
  phone?: string;
  department?: string;
  is_active?: boolean;
  user_id?: number;
  clear_user_id?: boolean;
  site_id?: number | null;
  clear_site?: boolean;
};

export function updateAdminHost(hostId: number, payload: AdminHostUpdatePayload) {
  return apiRequest<AdminHost>(`/admin/hosts/${hostId}`, {
    method: "PATCH",
    body: payload,
  });
}

export type InviteCreatePayload = {
  email: string;
  phone?: string;
  host_id: number;
  purpose: string;
  expected_exit?: string;
};

export type InviteCreateResponse = {
  visit_id: number;
  email: string;
  invite_url: string;
  expires_at: string;
  email_sent: boolean;
};

export function createInvite(payload: InviteCreatePayload) {
  return apiRequest<InviteCreateResponse>("/invites", {
    method: "POST",
    body: payload,
  });
}

export type DirectPassPayload = {
  full_name: string;
  email?: string;
  phone?: string;
  host_id: number;
  purpose: string;
  expected_exit?: string;
};

export type DirectPassResult = {
  visit_id: number;
  badge_code: string;
  pass_url: string;
  email: string | null;
  email_sent: boolean;
  whatsapp_sent: boolean;
};

export function createDirectPass(payload: DirectPassPayload) {
  return apiRequest<DirectPassResult>("/invites/direct", {
    method: "POST",
    body: payload,
  });
}

export function setHostDirectEntry(enabled: boolean) {
  return apiRequest<CompanySettings>("/admin/company-settings/host-direct-entry", {
    method: "PATCH",
    body: { enabled },
  });
}

export function listEmailTemplates() {
  return apiRequest<EmailTemplate[]>("/admin/email-templates");
}

export function updateEmailTemplate(templateKey: string, payload: { subject: string; body: string }) {
  return apiRequest<EmailTemplate>(`/admin/email-templates/${templateKey}`, {
    method: "PUT",
    body: payload,
  });
}

export function resetEmailTemplate(templateKey: string) {
  return apiRequest<EmailTemplate>(`/admin/email-templates/${templateKey}`, {
    method: "DELETE",
  });
}

export function testSendEmailTemplate(templateKey: string, to: string) {
  return apiRequest<{ sent: boolean; detail: string | null }>(
    `/admin/email-templates/${templateKey}/test-send`,
    { method: "POST", body: { to } },
  );
}

export function listGatePassTemplates() {
  return apiRequest<GatePassTemplate[]>("/admin/gate-pass-templates");
}

export type GatePassTemplateCreatePayload = {
  name: string;
  variant: string;
  accent_color?: string;
  footer_text?: string;
};

export function createGatePassTemplate(payload: GatePassTemplateCreatePayload) {
  return apiRequest<GatePassTemplate>("/admin/gate-pass-templates", {
    method: "POST",
    body: payload,
  });
}

export type GatePassTemplateUpdatePayload = {
  name?: string;
  variant?: string;
  accent_color?: string;
  footer_text?: string;
};

export function updateGatePassTemplate(id: number, payload: GatePassTemplateUpdatePayload) {
  return apiRequest<GatePassTemplate>(`/admin/gate-pass-templates/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

export function activateGatePassTemplate(id: number) {
  return apiRequest<GatePassTemplate>(`/admin/gate-pass-templates/${id}/activate`, {
    method: "POST",
  });
}

export function deleteGatePassTemplate(id: number) {
  return apiRequest<null>(`/admin/gate-pass-templates/${id}`, {
    method: "DELETE",
  });
}

export function getActiveGatePassTemplate() {
  return apiRequest<GatePassTemplate>("/dashboard/gate-pass-template");
}

export function getCompanySettings() {
  return apiRequest<CompanySettings>("/admin/company-settings");
}

export type CompanySettingsUpdatePayload = {
  approval_sla_warning_minutes: number;
  approval_sla_critical_minutes: number;
};

export function updateCompanySettings(payload: CompanySettingsUpdatePayload) {
  return apiRequest<CompanySettings>("/admin/company-settings", {
    method: "PUT",
    body: payload,
  });
}

export function setBrandingEnabled(enabled: boolean) {
  return apiRequest<CompanySettings>("/admin/company-settings/branding", {
    method: "PATCH",
    body: { enabled },
  });
}

// ---- Sites (multi-site) ----

export function listSites() {
  return apiRequest<Site[]>("/admin/sites");
}

export function createSite(payload: SiteCreatePayload) {
  return apiRequest<Site>("/admin/sites", {
    method: "POST",
    body: payload,
  });
}

export function updateSite(siteId: number, payload: SiteUpdatePayload) {
  return apiRequest<Site>(`/admin/sites/${siteId}`, {
    method: "PATCH",
    body: payload,
  });
}

export type SiteContext = {
  scoped: boolean;
  site_id: number | null;
  site_name: string | null;
  sites: { id: number; name: string }[];
};

export function getSiteContext() {
  return apiRequest<SiteContext>("/dashboard/site-context");
}

export function getEmailConfig() {
  return apiRequest<EmailConfig>("/admin/email-config");
}

export function updateEmailConfig(payload: EmailConfigUpdatePayload) {
  return apiRequest<EmailConfig>("/admin/email-config", {
    method: "PUT",
    body: payload,
  });
}

export function testEmailConfig(to: string) {
  return apiRequest<{ status: string; detail?: string }>("/admin/email-config/test", {
    method: "POST",
    body: { to },
  });
}

export function uploadCompanyLogo(file: File) {
  const form = new FormData();
  form.set("file", file);
  return apiRequest<CompanySettings>("/admin/company-settings/logo", {
    method: "POST",
    body: form,
  });
}

export function deleteCompanyLogo() {
  return apiRequest<CompanySettings>("/admin/company-settings/logo", {
    method: "DELETE",
  });
}

export function uploadCompanyFavicon(file: File) {
  const form = new FormData();
  form.set("file", file);
  return apiRequest<CompanySettings>("/admin/company-settings/favicon", {
    method: "POST",
    body: form,
  });
}

export function deleteCompanyFavicon() {
  return apiRequest<CompanySettings>("/admin/company-settings/favicon", {
    method: "DELETE",
  });
}

export function getBillingPlans() {
  return apiRequest<PlanInfo[]>("/billing/plans");
}

export function getSubscription() {
  return apiRequest<Subscription>("/billing/subscription");
}

export function createCheckout(plan: BillingPlanKey, billingCycle: BillingCycle) {
  return apiRequest<CheckoutSession>("/billing/checkout", {
    method: "POST",
    body: { plan, billing_cycle: billingCycle },
  });
}

export function verifyPayment(
  razorpayOrderId: string,
  razorpayPaymentId: string,
  razorpaySignature: string,
) {
  return apiRequest<Subscription>("/billing/verify", {
    method: "POST",
    body: {
      razorpay_order_id: razorpayOrderId,
      razorpay_payment_id: razorpayPaymentId,
      razorpay_signature: razorpaySignature,
    },
  });
}

// ── Long-term reusable passes ─────────────────────────────────────────────

export type RecurringPass = {
  id: number;
  visitor_name: string;
  host_name?: string | null;
  purpose: string;
  pass_type: string;
  valid_from: string;
  valid_until?: string | null;
  badge_code?: string | null;
  pass_url?: string | null;
  status: string;
  requested_by_host: boolean;
  created_at?: string | null;
};

export type RecurringPassCreatePayload = {
  full_name: string;
  host_id: number;
  purpose: string;
  pass_type: "permanent" | "monthly" | "yearly" | "custom";
  valid_from?: string | null;
  valid_until?: string | null;
  phone?: string | null;
  email?: string | null;
};

export function listRecurringPasses() {
  return apiRequest<RecurringPass[]>("/passes");
}

export function createRecurringPass(payload: RecurringPassCreatePayload) {
  return apiRequest<RecurringPass>("/passes", { method: "POST", body: payload });
}

export function approveRecurringPass(passId: number) {
  return apiRequest<RecurringPass>(`/passes/${passId}/approve`, { method: "POST" });
}

export function revokeRecurringPass(passId: number) {
  return apiRequest<RecurringPass>(`/passes/${passId}/revoke`, { method: "POST" });
}
